import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, Mail, Phone, MapPin, Calendar, MessageSquare, FileText, Image } from "lucide-react";
import { api, authHeader } from "../lib/api";
import { SITE } from "../lib/site";
import ProjectsManager from "../components/ProjectsManager";

const STATUS_OPTIONS = ["new", "read", "contacted", "closed"];

function StatBox({ label, value, accent }) {
    return (
        <div className="bg-card rounded-xl border border-border p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className={`mt-2 text-3xl font-heading font-extrabold ${accent ? "text-secondary" : "text-foreground"}`}>{value}</div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(localStorage.getItem("dandy_admin_token") || "");
    const [pwd, setPwd] = useState("");
    const [loading, setLoading] = useState(false);

    if (!token) return <Login onLogin={(t) => { localStorage.setItem("dandy_admin_token", t); setToken(t); }} pwd={pwd} setPwd={setPwd} loading={loading} setLoading={setLoading} />;

    return <Dashboard onLogout={() => { localStorage.removeItem("dandy_admin_token"); setToken(""); }} />;
}

function Login({ onLogin, pwd, setPwd, loading, setLoading }) {
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/admin/login", { password: pwd });
            onLogin(data.token);
            toast.success("Welcome back");
        } catch {
            toast.error("Invalid password");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="min-h-screen grid place-items-center bg-muted px-6" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-md bg-card rounded-xl border border-border p-8 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-extrabold">D</div>
                    <div>
                        <div className="font-heading font-bold">{SITE.name} Admin</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-[0.18em]">Dashboard access</div>
                    </div>
                </div>
                <label className="block">
                    <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Admin password</span>
                    <div className="relative">
                        <Lock size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
                        <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="bg-white border border-border rounded-md py-3 pl-10 pr-4 w-full focus:outline-none focus:border-primary" data-testid="admin-password-input" required />
                    </div>
                </label>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-btn">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                    {loading ? "Signing in…" : "Sign In"}
                </button>
                <p className="text-xs text-muted-foreground text-center">For team use only.</p>
            </form>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const [tab, setTab] = useState("inquiries");
    const [stats, setStats] = useState(null);
    const [inquiries, setInquiries] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [selected, setSelected] = useState(null);

    const loadAll = async () => {
        try {
            const [s, i, q] = await Promise.all([
                api.get("/admin/stats", { headers: authHeader() }),
                api.get("/admin/inquiries", { headers: authHeader() }),
                api.get("/admin/quotes", { headers: authHeader() }),
            ]);
            setStats(s.data); setInquiries(i.data); setQuotes(q.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired");
                onLogout();
            }
        }
    };

    useEffect(() => { loadAll(); }, []);

    const updateStatus = async (kind, id, status) => {
        try {
            await api.patch(`/admin/${kind}/${id}`, { status }, { headers: authHeader() });
            toast.success("Updated");
            loadAll();
            if (selected) setSelected({ ...selected, status });
        } catch {
            toast.error("Update failed");
        }
    };

    const list = tab === "inquiries" ? inquiries : quotes;

    return (
        <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
            <header className="bg-card border-b border-border sticky top-0 z-10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-extrabold text-sm">D</div>
                        <div className="font-heading font-bold">Dandy Admin</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/" className="text-sm text-muted-foreground hover:text-primary" data-testid="admin-back-link">View site →</a>
                        <button onClick={onLogout} className="text-sm text-muted-foreground hover:text-destructive flex items-center gap-1.5" data-testid="admin-logout-btn">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 space-y-8">
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-extrabold">Inbox</h1>
                    <p className="text-muted-foreground mt-1">All incoming enquiries and quote requests.</p>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
                        <StatBox label="Inquiries (total)" value={stats.inquiries_total} />
                        <StatBox label="Inquiries (new)" value={stats.inquiries_new} accent />
                        <StatBox label="Quotes (total)" value={stats.quotes_total} />
                        <StatBox label="Quotes (new)" value={stats.quotes_new} accent />
                    </div>
                )}

                {tab === "projects" ? (
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                        <div className="flex border-b border-border">
                            {[
                                { k: "inquiries", label: "Inquiries", icon: MessageSquare, count: inquiries.length },
                                { k: "quotes", label: "Quotes", icon: FileText, count: quotes.length },
                                { k: "projects", label: "Projects", icon: Image, count: null },
                            ].map(({ k, label, icon: Icon, count }) => (
                                <button
                                    key={k}
                                    onClick={() => { setTab(k); setSelected(null); }}
                                    className={`px-6 py-4 text-sm font-medium transition-colors ${tab === k ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
                                    data-testid={`admin-tab-${k}`}
                                >
                                    <Icon size={14} className="inline mr-2" />
                                    {label}{count !== null ? ` (${count})` : ""}
                                </button>
                            ))}
                        </div>
                        <ProjectsManager />
                    </div>
                ) : (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                    <div className="flex border-b border-border">
                        {[
                            { k: "inquiries", label: "Inquiries", icon: MessageSquare, count: inquiries.length },
                            { k: "quotes", label: "Quotes", icon: FileText, count: quotes.length },
                            { k: "projects", label: "Projects", icon: Image, count: null },
                        ].map(({ k, label, icon: Icon, count }) => (
                            <button
                                key={k}
                                onClick={() => { setTab(k); setSelected(null); }}
                                className={`px-6 py-4 text-sm font-medium transition-colors ${tab === k ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid={`admin-tab-${k}`}
                            >
                                <Icon size={14} className="inline mr-2" />
                                {label}{count !== null ? ` (${count})` : ""}
                            </button>
                        ))}
                    </div>

                    {list.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">No {tab} yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted">
                                    <tr className="text-left">
                                        <th className="py-3 px-5 font-medium text-muted-foreground uppercase text-xs tracking-wider">Date</th>
                                        <th className="py-3 px-5 font-medium text-muted-foreground uppercase text-xs tracking-wider">Name</th>
                                        <th className="py-3 px-5 font-medium text-muted-foreground uppercase text-xs tracking-wider">Email</th>
                                        <th className="py-3 px-5 font-medium text-muted-foreground uppercase text-xs tracking-wider">{tab === "inquiries" ? "Service" : "Project"}</th>
                                        <th className="py-3 px-5 font-medium text-muted-foreground uppercase text-xs tracking-wider">Status</th>
                                        <th className="py-3 px-5"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {list.map((item) => (
                                        <tr key={item.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => setSelected({ ...item, _kind: tab })} data-testid={`admin-row-${item.id}`}>
                                            <td className="py-3 px-5 text-muted-foreground whitespace-nowrap">{new Date(item.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</td>
                                            <td className="py-3 px-5 font-medium">{item.name}</td>
                                            <td className="py-3 px-5 text-muted-foreground">{item.email}</td>
                                            <td className="py-3 px-5">{tab === "inquiries" ? item.service || "—" : item.project_type}</td>
                                            <td className="py-3 px-5">
                                                <StatusPill status={item.status} />
                                            </td>
                                            <td className="py-3 px-5 text-right text-muted-foreground">View →</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                )}
            </main>

            {selected && (
                <DetailDrawer item={selected} onClose={() => setSelected(null)} onStatus={(s) => updateStatus(selected._kind, selected.id, s)} />
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const styles = {
        new: "bg-secondary/15 text-secondary",
        read: "bg-blue-100 text-blue-700",
        contacted: "bg-amber-100 text-amber-700",
        closed: "bg-primary/10 text-primary",
    };
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || ""}`}>{status}</span>;
}

function DetailDrawer({ item, onClose, onStatus }) {
    const isQuote = item._kind === "quotes";
    return (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="admin-detail-drawer">
            <aside className="absolute right-0 top-0 bottom-0 w-full md:w-[520px] bg-card overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-card border-b border-border p-6 flex items-start justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{isQuote ? "Quote request" : "Inquiry"}</div>
                        <h2 className="font-heading text-2xl font-extrabold mt-1">{item.name}</h2>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl" data-testid="admin-detail-close">✕</button>
                </div>

                <div className="p-6 space-y-5 text-sm">
                    <Row icon={<Mail size={14} />} label="Email"><a href={`mailto:${item.email}`} className="text-primary underline">{item.email}</a></Row>
                    {item.phone && <Row icon={<Phone size={14} />} label="Phone"><a href={`tel:${item.phone}`} className="text-primary">{item.phone}</a></Row>}
                    {item.address && <Row icon={<MapPin size={14} />} label="Address">{item.address}</Row>}
                    <Row icon={<Calendar size={14} />} label="Received">{new Date(item.created_at).toLocaleString("en-GB")}</Row>
                    {isQuote ? (
                        <>
                            <Row label="Project type">{item.project_type}</Row>
                            {item.project_size && <Row label="Size">{item.project_size}</Row>}
                            {item.budget && <Row label="Budget">{item.budget}</Row>}
                            {item.timeline && <Row label="Timeline">{item.timeline}</Row>}
                            <Row label="Description"><p className="whitespace-pre-wrap leading-relaxed">{item.description}</p></Row>
                        </>
                    ) : (
                        <>
                            {item.service && <Row label="Service">{item.service}</Row>}
                            <Row label="Message"><p className="whitespace-pre-wrap leading-relaxed">{item.message}</p></Row>
                        </>
                    )}

                    <div className="pt-4 border-t border-border">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Update status</div>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => onStatus(s)}
                                    data-testid={`admin-status-${s}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${item.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function Row({ icon, label, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1 flex items-center gap-1.5">
                {icon} {label}
            </div>
            <div>{children}</div>
        </div>
    );
}
